"use client";

import React, { useState } from "react";
import Image from "next/image";
import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { ASSET_PREFIX } from "@/config";

/* ---------------- List Item Component (Your Style Applied) ---------------- */

const StyledList: React.FC<{ items: string[] }> = ({ items }) => {
  return (
    <ul className="grid gap-[15px] sm:gap-[20px] mt-[10px]">
      {items.map((text, index) => (
        <li
          key={index}
          className="flex items-start gap-[10px] group/arrow hover:text-[#8d702b] transition duration-300 ease-in-out">
          <svg
            width="12"
            height="12"
            viewBox="0 0 16 16"
            className="transition-transform duration-300 group-hover/arrow:rotate-45  mt-[5px] min-w-[22px]">
            <path
              d="M15.435 1.565c0-.552-.448-1-1-1H5.435a1 1 0 0 0-1 1c0 .553.448 1 1 1H13.435V10.565c0 .552.448 1 1 1s1-.448 1-1V1.565ZM1 15l.707.707L15.142 2.272 14.435 1.565 13.728.858.293 14.293 1 15Z"
              fill="#b08d38"></path>
          </svg>
          <span>{text}</span>
        </li>
      ))}
    </ul>
  );
};

/* ---------------- Types ---------------- */

interface AccordionItem {
  id: number | string;
  title: string;
  content: React.ReactNode;
  disabled?: boolean;
}

interface AccordionProps {
  items: AccordionItem[];
  single?: boolean;
  defaultOpenIds?: (string | number)[];
  className?: string;
  itemClassName?: string;
}

/* ---------------- Accordion Component ---------------- */

const Accordion: React.FC<AccordionProps> = ({
  items,
  single = false,
  defaultOpenIds = [],
  className = "",
  itemClassName = "",
}) => {
  const [open, setOpen] = useState<Set<string | number>>(
    new Set(defaultOpenIds)
  );

  const toggle = (id: string | number, disabled?: boolean) => {
    if (disabled) return;

    setOpen((prev) => {
      const next = new Set(prev);
      if (single) {
        if (next.has(id)) next.clear();
        else {
          next.clear();
          next.add(id);
        }
      } else {
        next.has(id) ? next.delete(id) : next.add(id);
      }
      return next;
    });
  };

  return (
    <div
      className={
        "w-full divide-y divide-[#b08d38] border-b border-[#b08d38] bg-white " +
        className
      }>
      {items.map(({ id, title, content, disabled }, idx) => {
        const isOpen = open.has(id);
        const panelId = `accordion-panel-${id}`;
        const headingId = `accordion-heading-${id}`;

        return (
          <div
            key={id}
            className={
              "group/acc pr-4 sm:pr-6 " +
              (idx === 0 ? "rounded-t-2xl " : "") +
              (idx === items.length - 1 ? "rounded-b-2xl " : "") +
              itemClassName
            }>
            <button
              id={headingId}
              className={
                "flex w-full items-center justify-between gap-4 py-4 text-left outline-none transition-opacity cursor-pointer " +
                (disabled
                  ? "cursor-not-allowed opacity-50"
                  : "hover:opacity-90")
              }
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => toggle(id, disabled)}
              disabled={disabled}>
              <h2 className="text-[20px] leading-[28px] font-medium text-gray-900">
                {title}
              </h2>
              <div className="p-[10px] rounded-full bg-[#b1b1b1] w-[40px] h-[40px] flex items-center justify-center transition-transform duration-300">
                <ChevronDown
                  className={
                    "h-6 w-6 transition-transform text-[#000] " +
                    (isOpen ? "rotate-180" : "rotate-0")
                  }
                />
              </div>
            </button>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.section
                  key="content"
                  id={panelId}
                  role="region"
                  aria-labelledby={headingId}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.24, ease: [0.2, 0.8, 0.2, 1] }}
                  style={{ overflow: "hidden" }}>
                  <div className="pb-5 text-[16px] leading-[22px] sm:text-base leading-relaxed w-[100%] md:w-[90%] text-[#171717]">
                    {content}
                  </div>
                </motion.section>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

/* ---------------- Solar Blog Data ---------------- */

const solarBlogData = [
  {
    image: `/images/blogs/why-pluswilla-banner.webp`,
    title:
      "Why +Willa Has Built-In Solar Power: Save Your EB Bill for 25 Years & Earn ₹24.6 Lakhs in Return",
    date: "22 Nov 2025",
    content1: (
      <>
        <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
          Electricity demand is increasing faster than ever. Every new device
          adds to monthly bills - from refrigerators and air-conditioners to EV
          chargers and home automation. For most families, rising technology
          means rising bills. But what if your home could completely reverse
          that equation?
        </p>

        <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
          That’s exactly why +Willa has Built-In Solar Power - not as an
          upgrade, but as a default feature in every villa.
        </p>
      </>
    ),
  },
];

/* ---------------- Solar Accordion Items (List Applied) ---------------- */

const solarAccordionItems: AccordionItem[] = [
  {
    id: 1,
    title: "The Problem With Traditional Solar Installation",
    content: (
      <>
        <p className="text-[16px] leading-[24px] text-[#171717] mb-[10px]">
          If solar can save lakhs, why don’t all homes already have it?
        </p>
        <p className="text-[16px] leading-[24px] text-[#171717] mb-[10px]">
          Because for most people, solar installation is filled with confusion
          and risk:
        </p>

        <StyledList
          items={[
            "Where should panels be placed? ",
            "Will the home’s structure support it? ",
            "Which vendor is reliable? ",
            "What about approvals and wiring? ",
            "How will panels be cleaned and maintained? ",
          ]}
        />
        <p className="text-[16px] leading-[24px] text-[#171717] my-[10px]">
          The result ? Most people know solar is profitable - but don’t adopt it
          due to the complexity.
        </p>
      </>
    ),
  },
  {
    id: 2,
    title: "+Willa Solves Solar Completely - Before You Move In",
    content: (
      <>
        <p className="text-[16px] leading-[24px] text-[#171717] mb-[10px]">
          Instead of homeowners struggling to retrofit solar, +Willa makes solar
          effortless. Every villa comes with Built-In Solar Power, installed
          during construction - seamlessly integrated above the terrace deck.
        </p>

        <StyledList
          items={[
            "You don’t climb on rooftops. ",
            "You don’t coordinate with vendors. ",
            "You don’t wait for approvals. ",
            "You Just Move In. ",
          ]}></StyledList>

        <p className="text-[16px] leading-[24px] text-[#171717] my-[10px]">
          The system is already:
        </p>

        <StyledList
          items={["Installed ", " Tested ", " Approved ", "Fully operational "]}
        />
      </>
    ),
  },
  {
    id: 3,
    title: "Minimal-Effort Maintenance: Automatic Solar Panel Sprinklers ",
    content: (
      <>
        <p className="text-[16px] leading-[24px] text-[#171717] mb-[10px]">
          One of the biggest worries homeowners have is “Who will clean the
          panels?”
        </p>
        <p className="text-[16px] leading-[24px] text-[#171717] mb-[10px]">
          Dust reduces efficiency dramatically - but manual cleaning is risky
          and labour-dependent.
        </p>
        <p className="text-[16px] leading-[24px] text-[#171717] mb-[10px]">
          +Willa eliminates this problem with: Automatic Solar Panel Sprinkler
          System
        </p>
        <p className="text-[16px] leading-[24px] text-[#171717] mb-[10px]">
          The sprinklers keep the panels clean, ensuring maximum solar output
          with zero manual effort.{" "}
        </p>
        <p className="text-[16px] leading-[24px] text-[#171717] mb-[10px]">
          This means:
        </p>

        <StyledList
          items={[
            "No terrace climbing ",
            "No water tank and pipe setup ",
            "No drop in efficiency ",
            "No service appointments ",
          ]}
        />
      </>
    ),
  },
  {
    id: 4,
    title: "The Numbers: This Isn’t Just Sustainable - It’s Profitable",

    content: (
      <>
        <p className="text-[16px] leading-[24px] text-[#171717] mb-[10px]">
          The built-in solar system is not just environmentally friendly - it is
          financially powerful.
        </p>
        <StyledList
          items={[
            "Solar Capacity: 6 KVA On-Grid",
            "Units Generated Every Cycle: ≈ 1,800 units",
            "Annual Savings: ≈ ₹1.3 lakhs",
            "ROI: ≈ 3 years",
            "Net Profit in 22 Years: ≈ ₹24.6 lakhs",
            "Total EB Bill for 25 Years: ₹0",
          ]}
        />
        <p className="text-[16px] leading-[24px] text-[#171717] my-[10px]">
          This means your home generates wealth - instead of costing you every
          month.
        </p>
      </>
    ),
  },
  {
    id: 5,
    title: "Live Net Zero From Day 1 - Just Move In",

    content: (
      <>
        <p className="text-[16px] leading-[24px] text-[#171717] mb-[10px]">
          The biggest advantage of +Willa is that your savings start
          immediately.
        </p>
        <StyledList
          items={[
            "You move in → Your EB Bill becomes ₹0 ",
            "You live → You generate savings every year ",
            "You stay → You generate ₹24.6 lakhs in long-term profit ",
            "No waiting ",
            "No installation  ",
            "No risk  ",
            "Just guaranteed, sustainable living from the first day. ",
          ]}
        />
      </>
    ),
  },
  {
    id: 6,
    title: "Final Thoughts ",

    content: (
      <>
        {" "}
        <p className="text-[16px] leading-[24px] text-[#171717] mb-[10px]">
          +Willa homes don’t just save energy - they generate wealth.
        </p>
        <p className="text-[16px] leading-[24px] text-[#171717] mb-[10px]">
          With rising electricity costs and the world moving toward sustainable
          living, +Willa is not only future-proof - it is financially wise.
        </p>
        <p className="text-[16px] leading-[24px] text-[#171717] mb-[10px]">
          If you want a home that:
        </p>
        <StyledList
          items={[
            "Saves money for 25 years ",
            "Offers zero EB Bill ",
            "Requires zero setup",
            "Supports a healthier planet",
          ]}
        />
        <p className="text-[16px] leading-[24px] text-[#171717] my-[10px]">
          Then +Willa is not just a better home - it is a smarter investment.
        </p>
      </>
    ),
  },
];

/* ---------------- Accordion Demo Wrapper ---------------- */

const AccordionDemo = () => (
  <Accordion items={solarAccordionItems} single defaultOpenIds={[1]} />
);

/* ---------------- Page Component ---------------- */

export default function BuiltInSolarBlog() {
  return (
    <div className="sticky top-[130px] z-[2]">
      {solarBlogData.map((item, index) => (
        <div className="group relative" key={index}>
          <h1 className="text-[28px]/[36px] md:text-[32px]/[40px] lg:text-[36px]/[44px] xl:text-[40px]/[48px] 2xl:text-[48px]/[54px] tracking-[-0.03em] font-[500] text-[var(--secondary)]">
            {item.title}
          </h1>
          <p className="text-[#924000] my-[20px] mb-[50px] max-mlg:mb-[30px] text-[20px] leading-[26px]  max-xl:my-[15px] ">
            {item.date}
          </p>

          <div className="rounded-[10px] overflow-hidden">
            <Image
              src={`${ASSET_PREFIX}${item.image}`}
              width={530}
              height={500}
              alt="Blog banner"
              unoptimized
              className="w-full max-h-[500px] object-cover group-hover:scale-[1.1] transition duration-300 ease-linear"
            />
          </div>

          <div className="mt-[50px]">
            <p className="my-[20px] text-[20px] leading-[28px] font-light group-hover:text-[#8d702b] transition-colors duration-300">
              {item.title}
            </p>

            <div className="text-[16px] leading-[24px] mb-[20px] font-light">
              {item.content1}
            </div>
          </div>

          <div className="mb-[20px]">
            <AccordionDemo />
          </div>
        </div>
      ))}
    </div>
  );
}
